import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {Handle, HandleProps, HandleType, Position, useStore} from 'reactflow';

import * as Colors from 'ui-library/Colors';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import AddIcon from 'ui-library/icons/AddIcon';
import {isEscapeKey} from 'util/Keyboard';
import {ElementKeyboardEvent, ElementMouseEvent, MakeFieldsRequired} from 'util/Types';

const HANDLE_DOT_WIDTH = 8;
const ICON_WIDTH = 12;
const ICON_PADDING = 2.5;
const ICON_BORDER_WIDTH = 1.5;

const TOTAL_ICON_WIDTH = ICON_WIDTH + ICON_PADDING * 2 + ICON_BORDER_WIDTH * 2;
const SPACE_BETWEEN_NODE_AND_HANDLE = TOTAL_ICON_WIDTH / 2 + HANDLE_DOT_WIDTH;

/**
 * Position handles slightly away from the node, horizontally, so the handles appear to "float" alongsize it,
 * and to provide room for <AddNodeIcon>.
 *
 * `verticalOffset` is provided by the caller, since nodes may have several handles, or different styles.
 */
function calculateTranslation(type: HandleType, verticalOffset?: number): string {
  // Space the handle away from the node, so it appears to 'float' beside it, and has room for the add icon
  const translateX = type === 'target' ? -SPACE_BETWEEN_NODE_AND_HANDLE : SPACE_BETWEEN_NODE_AND_HANDLE;
  // Adjust for the node's height, so the handle is vertically centered along the node's side.
  const translateY = (verticalOffset ?? 0) - TOTAL_ICON_WIDTH / 2;
  return `translate(${translateX}px, ${translateY}px)`;
}

export type AddNodeHandleProps = MakeFieldsRequired<Omit<HandleProps, 'position'>, 'id'> & {
  /**
   * If disabled, the handle will not be interactive.
   */
  disabled?: boolean;
  /**
   * Popover content, such as a `<Menu>`, to show when the handle is clicked.
   *
   * If `renderPopoverContent` is passed, the handle will show a "+" icon on hover, which acts as a popover
   * toggle.
   *
   * The popover is closed after the user clicks inside.
   *
   * To detect what the user has clicked, include callbacks within the content.
   */
  renderPopoverContent?: () => JSX.Element;
  /**
   * Called when the user clicks the handle.
   *
   * If `onClick` is passed (without `renderPopoverContent`), the handle will show a "+" icon on hover, which
   * acts like a button.
   *
   * (If `renderPopoverContent` is also passed, `onClick` is called when the user clicks the handle to open
   * or close the menu.)
   */
  onClick?: () => void;
  /**
   * Adjust where the handle is positioned vertically, relative to its default position. Negative values move
   * the handle up.
   *
   *
   * `reactflow` positions handles at `top: 50%`. Typically, this is relative to the node's height.
   *
   * If, however, a parent `<div>` of the handle has a `relative`, `absolute` or `fixed` position, the handle
   * will be positioned relative to that `<div>`.
   *
   * This provides two strategies to positioning the handle:
   * • If the handle should appear beside a specific element in the node:
   *     • Apply `position: relative` to a `<div>` beside which the handle should appear, and make
   *       `<AddNodeHandle>` a child of that `div`.
   * • If multiple handles should be spread out along the node's side, without regard to its content:
   *     • Set `verticalOffset` to translate the handle up or down.
   */
  verticalOffset?: number;
};

/**
 * An interactive handle which displays a "+" icon on hover.
 *
 * This handle can live many lives:
 * • Show a "+" button on hover, if `onClick` is provided
 * • Show a "+" menu or popover popover toggle on hover, if `renderPopoverContent` is provided
 * • Act as draggable edge handle, if `isConnectable` is `true`
 * • Be a simple read-only indicator that a node could be connected, if `disabled` is `true`
 *
 * This component assumes the flowchart is arranged left-to-right.
 */
export default function AddNodeHandle({
  disabled,
  onClick,
  renderPopoverContent,
  verticalOffset,
  ...handleProps
}: AddNodeHandleProps): JSX.Element {
  const [isHandleHovered, setIsHandleHovered] = useState<boolean>(false);
  const [isAddIconPressed, setAddIconIsPressed] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  // With `reactflow` v12 (`@xyflow/react`), we could use the narrower `useConnection` instead of `useStore`

  // `connectionHandleId` is the handle from which the connection was started
  const connectionHandleId = useStore((state) => state.connectionHandleId);

  // If the user is actively connecting edges, handles shouldn't activate our custom interactivity
  const isConnectingFromOtherHandle = connectionHandleId != null && connectionHandleId !== handleProps.id;

  const positionProps = {
    position: handleProps.type === 'target' ? Position.Left : Position.Right,
    style: {transform: calculateTranslation(handleProps.type, verticalOffset)},
  };

  /**
   * If there are no custom interactions, or the component is `disabled`, return a basic handle early, so it
   * is straightforward to disable interactivity.
   *
   * Similarly, don't show the interactive icon if the user may be dragging an edge to this node.
   *
   * Everything beyond this early return can assume the add icon is enabled.
   */
  const suppressAddIcon = disabled || !(onClick || renderPopoverContent) || isConnectingFromOtherHandle;
  if (suppressAddIcon) {
    return (
      <Handle
        {...handleProps}
        {...positionProps}
        className={css(handleStyles.handle)}
        isConnectable={disabled ? false : handleProps.isConnectable}
      >
        <div className={css(handleStyles.handleDot)} />
      </Handle>
    );
  }

  /**
   * Depending on which interactivity is enabled, we sometimes add event handlers to `Handle`, and other times
   * to its child `div`.
   *
   * For `Handle` to be connectable (`isConnectable: true`), mouse events must originate on the `div` rendered
   * by `Handle`, and not any of its child elements. (`reactflow` adds several internal HTML classes to the
   * `div` rendered by `Handle`, and ignores crucial mouse events from elements which lack those classes.)
   *
   * In contrast, if `Handle` is not meant to be connectable (`isConnectable: false`), it ignores mouse events
   * for itself, by applying `pointer-events: none`. In this case, we cannot add our event handlers to
   * `Handle` itself, which would ignore them.
   *
   * If `isConnectable` is `true`, we:
   * • Set `pointer-events: none` on the child elements, so no events originate from them.
   * • Set `eventHandlerProps` on `Handle`.
   *
   * If `isConnectable` if `false`, we:
   * • Set `pointer-events: all` on the child `div`, so events are enabled for the child elements.
   * • Set `eventHandlerProps` on the child `div`, since `Handle` will ignore them.
   */
  const handleEventsOnChild = isPopoverOpen || !handleProps.isConnectable;

  const eventHandlerProps = {
    onMouseEnter: (e: ElementMouseEvent) => {
      setIsHandleHovered(true);
      e.stopPropagation();
    },
    onMouseLeave: () => {
      setIsHandleHovered(false);
      // Don't show the icon as pressed, if the user has dragged their cursor away from the icon
      setAddIconIsPressed(false);
    },
    onMouseDown: (e: ElementMouseEvent) => {
      // Track if the icon is pressed, so we can show a different background color on the add icon
      setAddIconIsPressed(true);
      e.stopPropagation();
    },
    onClick: (e: ElementMouseEvent) => {
      onClick?.();
      if (renderPopoverContent) {
        setIsPopoverOpen(!isPopoverOpen);
      }
      setAddIconIsPressed(false);
      e.stopPropagation(); // Clicks within handles shouldn't be treated as clicks within nodes
    },
    onKeyDown: (e: ElementKeyboardEvent) => {
      if (isEscapeKey(e)) {
        setIsPopoverOpen(false);
      }
    },
  };

  return (
    <Handle
      {...handleProps}
      {...positionProps}
      {...(handleEventsOnChild ? {} : {...eventHandlerProps})}
      // Use `pointer: cursor`, since the add icon acts like a button
      className={css(handleStyles.handle, handleStyles.pointerCursor)}
    >
      <div className={css(handleStyles.handleDot)} />
      <div
        {...(handleEventsOnChild ? {...eventHandlerProps} : {})}
        className={css(
          addIconStyles.addIconWrapper,
          // `reactflow` drag-and-drop connecting only works if events originate from the div rendered by
          // `Handle`; only enable pointer events on the child if drag-and-drop connecting is disabled, in
          // which case `Handle` applies `pointer-events: none` to itself, which must be overriden.
          handleEventsOnChild ? addIconStyles.pointerEventsEnabled : addIconStyles.pointerEventsDisabled
        )}
      >
        {(isHandleHovered || isPopoverOpen) && (
          <AddIcon
            className={css(
              addIconStyles.addIcon,
              isPopoverOpen && addIconStyles.isPopoverOpen, // Rotate '+' to an '✕', to cancel the popover
              // Show a darker background color while the mouse is down
              isAddIconPressed && renderPopoverContent == null && addIconStyles.isAddIconPressed,
              isAddIconPressed && renderPopoverContent != null && addIconStyles.isAddIconPressedBeforePopover
            )}
          />
        )}
        {renderPopoverContent && (
          <div // Wrap `Float` in a `div` to catch events from within `Float`
            className={css(addIconStyles.popoverAnchor)} // Prevent this `div` from itself taking up space
            onMouseEnter={(e) => {
              // Prevent clicks from within the popover from being treated as clicks on the icon
              e.stopPropagation();
              // Ensure hovering over the popover registers as leaving the icon
              setIsHandleHovered(false);
            }}
            onMouseLeave={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              // Close the popover when the user clicks within it.
              // In the future, we could make the close-on-click behavior configurable, for more complex
              // popover content.
              e.stopPropagation();
              setIsPopoverOpen(false);
              setIsHandleHovered(false);
              setAddIconIsPressed(false);
            }}
          >
            <Float
              interaction={FloatingInteraction.CONTROLLED}
              isOpen={isPopoverOpen}
              floatingElementOptions={{renderArrow: () => null}}
              placementOptions={{offsetOverride: 0}}
              onRequestClose={() => {
                // Close the popover if `Popover` detects the user has clicked outside it – unless the user
                // is hovering over the add icon, in which case they are likely clicking on the icon to close
                // the menu. Allow the icon click to close the popover instead.
                if (!isHandleHovered) {
                  setIsPopoverOpen(false);
                }
              }}
              renderContent={renderPopoverContent}
            >
              <></>
            </Float>
          </div>
        )}
      </div>
    </Handle>
  );
}

const handleStyles = StyleSheet.create({
  handle: {
    // The actual handle is a large transparent circle, the same size as the add icon, to maintain a
    // consistent hover target. We cannot rely on `iconWrapper` being a child element of `handle` to widen the
    // hover target, since the child element sometimes has `pointer-events: none` applied.
    background: 'transparent',
    border: 'none', // Remove the default, white `reactflow` border
    borderRadius: TOTAL_ICON_WIDTH / 2,
    height: TOTAL_ICON_WIDTH,
    width: TOTAL_ICON_WIDTH,
  },
  handleDot: {
    // Show a small, light gray circle within the larger target of `handle`. Visually, this is the "handle",
    // which is displayed whenenver the add icon isn't shown.
    background: Colors.GRAY_DARK,
    borderRadius: HANDLE_DOT_WIDTH / 2,
    height: HANDLE_DOT_WIDTH,
    width: HANDLE_DOT_WIDTH,
    // Align within center of transparent `handle`
    margin: (TOTAL_ICON_WIDTH - HANDLE_DOT_WIDTH) / 2,
    // Child elements of `handle` cannot originate mouse events, for the handle to be connectable
    pointerEvents: 'none',
  },
  pointerCursor: {
    // Do not use the default `crosshair` when showing the add icon, which behaves like a button; apply to
    // `Handle` rather than child elements because `cursor` won't be respected on child elements which have
    // `pointer-events: none`.
    cursor: 'pointer',
  },
});

const addIconStyles = StyleSheet.create({
  addIcon: {
    background: Colors.WHITE,
    border: `solid ${ICON_BORDER_WIDTH}px ${Colors.BLUE}`,
    borderRadius: TOTAL_ICON_WIDTH / 2,
    color: Colors.BLUE,
    height: ICON_WIDTH,
    padding: ICON_PADDING,
    transition: 'transform 300ms', // Animate the rotation to and from `isPopoverOpen`
    width: ICON_WIDTH,
  },
  addIconWrapper: {
    // Give the wrapper the same overall dimensions as the icon, so the hover target size remains consistent
    borderRadius: TOTAL_ICON_WIDTH / 2,
    height: TOTAL_ICON_WIDTH,
    width: TOTAL_ICON_WIDTH,
    // Position exactly over `handle`, which shares the same dimensions
    position: 'absolute',
    top: 0,
    left: 0,
  },
  isPopoverOpen: {
    background: Colors.BLUE_POWDER,
    transform: 'rotate(45deg)', // Rotate '+' to '✕',
  },
  isAddIconPressed: {
    background: Colors.BLUE_POWDER,
  },
  isAddIconPressedBeforePopover: {
    background: Colors.BLUE_LIGHTEST, // Provide contrast before the steady background in `isPopoverOpen`
  },
  pointerEventsDisabled: {
    pointerEvents: 'none',
  },
  pointerEventsEnabled: {
    pointerEvents: 'all',
  },
  popoverAnchor: {
    display: 'contents', // Keep this wrapper from itself taking up space
  },
});
